import { useState, useEffect, useCallback } from "react";

// ═══ THEME ═══
const C = {
  bg: "#07080A", sf: "#0E1013", cd: "#151719", el: "#1D1F23", bd: "#2A2D32",
  gd: "#C8A54E", gn: "#4ADE80", rd: "#F87171", or: "#FBBF24",
  bl: "#60A5FA", pr: "#A78BFA", cy: "#22D3EE",
  t1: "#F0EDE5", t2: "#9A978F", t3: "#5E5C57"
};

function sColor(s) {
  if (s >= 70) return C.gn;
  if (s >= 50) return C.or;
  return C.rd;
}

function sTier(s) {
  if (s >= 80) return "Leader";
  if (s >= 60) return "Challenger";
  if (s >= 40) return "At Risk";
  return "In Crisis";
}

// ═══ DIMENSIONS & METRICS ═══
const DIMS = [
  { id: "vitality", nm: "Brand Vitality", sh: "Vitality", ic: "\u{1F493}", cl: C.gd,
    ms: [
      { id: "awareness", nm: "Unaided Brand Awareness", wt: 25 },
      { id: "sov", nm: "Share of Voice", wt: 20 },
      { id: "search_vol", nm: "Branded Search Trend", wt: 20 },
      { id: "media", nm: "Media & PR Presence", wt: 15 },
      { id: "community", nm: "Community Strength", wt: 20 }
    ]},
  { id: "relevance", nm: "Relevance Quotient", sh: "Relevance", ic: "\u{1F3AF}", cl: C.bl,
    ms: [
      { id: "brand_fit", nm: "Brand-Market Fit", wt: 25 },
      { id: "trend", nm: "Trend Alignment", wt: 20 },
      { id: "diff", nm: "Competitive Differentiation", wt: 25 },
      { id: "premium", nm: "Price Premium", wt: 15 },
      { id: "consider", nm: "Category Consideration", wt: 15 }
    ]},
  { id: "profit", nm: "Profitability Architecture", sh: "Profit", ic: "\u{1F4B0}", cl: C.gn,
    ms: [
      { id: "margin", nm: "Gross Margin Health", wt: 25 },
      { id: "cac_ltv", nm: "CAC:LTV Ratio", wt: 25 },
      { id: "channel", nm: "Channel Profitability", wt: 20 },
      { id: "rev_conc", nm: "Revenue Concentration", wt: 15 },
      { id: "brand_val", nm: "Brand Premium Value", wt: 15 }
    ]},
  { id: "experience", nm: "Experience Coherence", sh: "Experience", ic: "\u{2728}", cl: C.pr,
    ms: [
      { id: "nps", nm: "Net Promoter Score", wt: 25 },
      { id: "touchpoint", nm: "Touchpoint Consistency", wt: 25 },
      { id: "digital", nm: "Digital Experience", wt: 20 },
      { id: "service", nm: "Service Quality", wt: 15 },
      { id: "retail", nm: "Physical/Retail Experience", wt: 15 }
    ]},
  { id: "growth", nm: "Growth Velocity", sh: "Growth", ic: "\u{1F4C8}", cl: C.or,
    ms: [
      { id: "rev_growth", nm: "Revenue Growth Rate", wt: 25 },
      { id: "mkt_expand", nm: "Market Expansion", wt: 20 },
      { id: "new_cust", nm: "Customer Acquisition", wt: 20 },
      { id: "innovation", nm: "Innovation Pipeline", wt: 20 },
      { id: "retention", nm: "Customer Retention", wt: 15 }
    ]},
  { id: "future", nm: "Future Readiness", sh: "Future", ic: "\u{1F52E}", cl: C.cy,
    ms: [
      { id: "dig_mat", nm: "Digital Maturity", wt: 25 },
      { id: "ai_ready", nm: "AI & Data Readiness", wt: 20 },
      { id: "talent", nm: "Talent Brand", wt: 20 },
      { id: "sustain", nm: "Sustainability", wt: 15 },
      { id: "adapt", nm: "Adaptability", wt: 20 }
    ]}
];

// ═══ 750 QUESTIONS (25 per metric) ═══
const QB = {};
QB.awareness = [
  "What % of your target audience can name your brand without prompts?",
  "Have you conducted an unaided recall study in the last 12 months?",
  "How does your unaided recall compare to the category leader?",
  "What % of your target market has heard of your brand (aided)?",
  "Is awareness higher among your core demographic vs general population?",
  "Do consumers correctly associate your brand with the right category?",
  "Has brand awareness increased, stayed flat, or declined this year?",
  "Can consumers identify your brand from visual assets alone?",
  "What is your top-of-mind awareness within your primary category?",
  "Do you systematically measure awareness across geographies?",
  "How many touchpoints before a new customer makes a purchase?",
  "Is brand awareness consistent across online and offline channels?",
  "Do you track prompted vs unprompted awareness separately?",
  "What % of category consumers have never heard of your brand?",
  "Is your brand frequently confused with a competitor?",
  "Do consumers know what your brand stands for beyond the product?",
  "How does awareness vary across different age demographics?",
  "Is your brand recognized by people outside your core target?",
  "Do trade partners spontaneously mention your brand?",
  "Have major awareness campaigns been run in the last 6 months?",
  "Is your brand name easy to recall, spell, and pronounce?",
  "Do customers mention you when recommending in your category?",
  "What is your awareness penetration in secondary markets?",
  "Is awareness growing faster or slower than marketing spend?",
  "Do new recruits already know your brand before joining?"
];
QB.sov = [
  "What % of total category social mentions belong to your brand?",
  "Is your share of voice growing or declining vs competitors?",
  "Do you track SOV across platforms (Instagram, X, YouTube, LinkedIn)?",
  "How does organic mention volume compare to paid mentions?",
  "Are consumers creating UGC featuring your brand?",
  "What is the ratio of positive to negative mentions online?",
  "Do influencers mention your brand organically (no paid)?",
  "Is your brand part of trending conversations in your category?",
  "How does mention frequency compare to your top 3 competitors?",
  "Do industry publications reference your brand regularly?",
  "Is your brand mentioned in review platforms and forums?",
  "What % of category hashtags include your brand?",
  "Are competitors outspending you but getting lower engagement?",
  "Do you monitor SOV in real-time or only periodically?",
  "Is your brand voice distinct enough to identify without a logo?",
  "How does earned media SOV compare to paid media SOV?",
  "Are employees amplifying your brand message?",
  "Do customers tag your brand in their posts regularly?",
  "Is SOV higher during key seasonal or campaign periods?",
  "How does video SOV compare to static content SOV?",
  "Are you gaining SOV in emerging platforms?",
  "Do competitors react to your campaigns (competitive awareness)?",
  "Is your brand mentioned in podcast and audio content?",
  "What is your estimated share of WhatsApp/community conversations?",
  "Does SOV translate into measurable business outcomes?"
];
QB.search_vol = [
  "Has branded search volume increased year-over-year?",
  "What is the monthly search volume for your brand name?",
  "How does branded search compare to top 3 competitors?",
  "Is your brand searched more on mobile or desktop?",
  "Do consumers search for brand + specific product terms?",
  "Are misspelling variants being searched frequently?",
  "Is branded search growing faster than category generic search?",
  "Do you rank #1 for your own brand name on Google?",
  "Are competitors bidding on your brand keywords?",
  "What % of website traffic comes from branded searches?",
  "Is your brand searched with positive terms (reviews, best, buy)?",
  "Do search trends spike around your campaign periods?",
  "Is your brand auto-suggested when typing your category?",
  "Does branded search volume correlate with sales data?",
  "Are consumers searching for you on YouTube and social?",
  "Is your brand searched in regional languages?",
  "Do brand vs competitor comparison searches exist?",
  "Has your search share of category grown?",
  "Are customers searching brand + location (purchase intent)?",
  "Do Google Trends show sustained or spike-decline patterns?",
  "Are you appearing in People Also Ask for category queries?",
  "How does search visibility compare during festivals/seasons?",
  "Are new segments discovering your brand through search?",
  "Do you track branded search across Google, YouTube, Amazon?",
  "Is your brand generating more searches than category terms?"
];
QB.media = [
  "How many earned media mentions in the past quarter?",
  "Are you featured in tier-1 industry publications?",
  "Do journalists proactively reach out for quotes or stories?",
  "Has your brand received industry awards or recognition?",
  "Is your founder/leadership visible as a thought leader?",
  "Do you have an active PR strategy with defined targets?",
  "Has your brand been covered in national media?",
  "Is media coverage predominantly positive, neutral, or negative?",
  "Do you track AVE of earned media?",
  "Are press releases picked up consistently?",
  "Has your brand been featured in case studies or reports?",
  "Do you have relationships with 10+ relevant journalists?",
  "Is your brand invited to speak at industry events?",
  "How does coverage frequency compare to competitors?",
  "Are you featured in year-end roundups and best-of lists?",
  "Do you have a crisis communication plan in place?",
  "Is coverage aligned with your brand positioning?",
  "Do you have a dedicated PR resource?",
  "Has negative press gone unaddressed?",
  "Are product launches covered without paid promotion?",
  "Is your brand mentioned in analyst/consultant reports?",
  "Do podcast hosts reference your brand?",
  "Is media presence consistent across your markets?",
  "Have you secured exclusive media partnerships or columns?",
  "Does media presence contribute to measurable awareness lifts?"
];
QB.community = [
  "Do you have active brand advocates who promote voluntarily?",
  "What % of customers have referred your brand to others?",
  "Do customers create and share content featuring your products?",
  "Is your social engagement rate above category average?",
  "Do you have a loyalty program or community membership?",
  "How active are brand communities on WhatsApp/Discord/FB Groups?",
  "Do customers defend your brand in online discussions?",
  "What is your average review rating across platforms?",
  "Do customers participate in co-creation or feedback initiatives?",
  "Is there a thriving resale market for your products?",
  "Do customers share unboxing or first-experience content?",
  "Have customers organized fan pages or unofficial communities?",
  "Do you have a formal brand ambassador program?",
  "What % of engagement comes from repeat engagers?",
  "Do customers provide testimonials willingly?",
  "Is your referral rate growing month-over-month?",
  "Do you track and respond to every customer mention?",
  "Are customers emotionally attached to your brand?",
  "Do community members help new customers with questions?",
  "Is email engagement above industry benchmarks?",
  "Do customers attend your brand events?",
  "Have you nurtured your top 100 most engaged customers?",
  "Do customers volunteer feedback and improvement suggestions?",
  "Is your community demographically aligned with your target?",
  "Does community strength lower customer acquisition costs?"
];
QB.brand_fit = [
  "Does positioning directly address a clearly defined consumer need?",
  "Have you validated your value proposition with consumers recently?",
  "Can you state your brand reason for existing in one sentence?",
  "Do consumers describe your brand the way you intend?",
  "Is there a gap between brand promise and delivery?",
  "Do surveys confirm positioning resonates with target segments?",
  "Has your target consumer primary need shifted in 2 years?",
  "Are you solving a problem consumers actively seek solutions for?",
  "Do customers cite your core benefit as reason for choosing you?",
  "Is your brand relevant to the daily life of your consumer?",
  "Would your audience miss your brand if it disappeared?",
  "Do you regularly conduct consumer needs assessments?",
  "Is your product adapted to local cultural preferences?",
  "Are you meeting functional needs, emotional needs, or both?",
  "Do consumers see your brand as essential or optional?",
  "Has brand-market fit improved or declined in the last year?",
  "Do adjacent segment consumers also find your brand relevant?",
  "Is your brand solving today problems or yesterday problems?",
  "Do you track shifting priorities and adapt positioning?",
  "Are younger consumers finding your brand relevant?",
  "Is your brand positioned for aspiration or current reality?",
  "Do consumers trust your expertise in your category?",
  "Does research support that price matches perceived value?",
  "Are you relevant across all usage occasions in your category?",
  "Would consumers recommend you to someone with the same need?"
];
QB.trend = [
  "Is your brand aligned with dominant cultural trends in your category?",
  "Have you adapted messaging to reflect current consumer values?",
  "Does your brand participate authentically in cultural moments?",
  "Are you tracking macro trends that could disrupt your category?",
  "Is your visual identity current or does it feel dated?",
  "Do consumers perceive your brand as modern and forward-thinking?",
  "Are you tapping into emerging lifestyle trends?",
  "Does content reflect how your audience actually communicates?",
  "Have you adapted channel strategy to evolving media consumption?",
  "Is your brand present where cultural conversations happen?",
  "Do you have a process for responding to micro-trends quickly?",
  "Is product development informed by trend forecasting?",
  "Are you leading trends in your category or only following?",
  "Does your brand take positions on issues your audience cares about?",
  "Is your tone contemporary without being trend-chasing?",
  "Have you updated brand identity elements in the last 3 years?",
  "Do consumers see your brand as culturally in-touch?",
  "Are you adapting to the authenticity and transparency shift?",
  "Is your brand relevant in India rapidly evolving consumer culture?",
  "Do you monitor global trends that eventually reach your market?",
  "Is your brand embracing digital-first consumer lifestyles?",
  "Are you perceived as innovative or traditional?",
  "Does your brand story connect with generational narratives?",
  "Are you adapting to premiumization in Indian markets?",
  "Is your brand positioned for health and wellness megatrends?"
];
QB.diff = [
  "Can you articulate 3 reasons to choose you over the market leader?",
  "Do consumers perceive meaningful differences vs competitors?",
  "Is differentiation based on something hard to replicate?",
  "Do you own a distinct territory no competitor claims?",
  "Has differentiation been validated through consumer research?",
  "Can your sales team articulate differentiation consistently?",
  "Is differentiation visible across all touchpoints?",
  "Do customers cite unique attributes for choosing your brand?",
  "Are you competing on value, not just price?",
  "Would a competitor customer switch for reasons beyond price?",
  "Is your brand the only one that can claim your key benefit?",
  "Do industry analysts recognize your differentiation?",
  "Has your competitive position strengthened or weakened?",
  "Are new entrants mimicking your positioning?",
  "Is differentiation based on product, brand meaning, or both?",
  "Do consumers associate unique positive attributes with you?",
  "Is your competitive moat widening or narrowing?",
  "Can you defend your premium with differentiation evidence?",
  "Is your brand name distinctive and memorable in the category?",
  "Do you have proprietary technology or processes that differentiate?",
  "Is your customer experience meaningfully different?",
  "Do you benchmark positioning against competitor messaging?",
  "Are you differentiated in distribution or availability?",
  "Is your brand story distinct, not interchangeable with competitors?",
  "Would removing your name from communications still feel uniquely you?"
];
QB.premium = [
  "Can you charge above category average and maintain volume?",
  "Have you tested price increases and measured elasticity?",
  "Do consumers perceive pricing as fair for value delivered?",
  "Is your premium justified by quality, brand equity, or both?",
  "What % of customers buy at full price vs only on promotion?",
  "Can competitors undercut you on price and steal customers?",
  "Has your price premium grown, stabilized, or eroded?",
  "Do consumers trade down from you during economic pressure?",
  "Is your premium sustainable if a competitor matches quality?",
  "Do you have pricing power with trade/retail partners?",
  "Is your brand perceived as worth the premium by non-customers?",
  "Have you mapped price positioning vs all competitors?",
  "Do customers justify price by brand value, not just product?",
  "Is pricing consistent across all channels?",
  "Can you successfully launch premium extensions?",
  "Do trade partners maintain your suggested retail prices?",
  "Is your price elasticity better than your closest competitor?",
  "Have promotions eroded your perceived price premium?",
  "Do customers associate quality with you before trying product?",
  "Is pricing strategy aligned with brand positioning?",
  "Can you reduce promos without losing volume?",
  "Do customers buy higher-priced variants preferentially?",
  "Is your perceived value-for-money ratio positive?",
  "Are you protecting premium positioning on marketplaces?",
  "Would a 10% price increase cause less than 10% volume loss?"
];
QB.consider = [
  "Is your brand in the top 3 consideration sets in your category?",
  "When consumers think of buying your category, do they think of you?",
  "What is your rank in online category searches?",
  "Do consumers actively seek your brand vs buying what is available?",
  "Is your brand the default for any specific usage occasion?",
  "What % of category buyers have tried your brand at least once?",
  "Are you gaining or losing consideration set share vs last year?",
  "Do retail partners stock you at eye level or premium positions?",
  "Is your brand a mandatory stock item for retailers?",
  "Do consumers include you in shortlists even without trying?",
  "Are you in category recommendation articles and best-of lists?",
  "Are you the most-reviewed brand in your category?",
  "Do sales associates recommend your brand proactively?",
  "What is your market share in your primary geography?",
  "Are you gaining consideration from competitor-switchers?",
  "Are you present in all retail formats where your category sells?",
  "Do consumers search your brand + review or vs before buying?",
  "Are you listed correctly in all marketplace categories?",
  "Are you the price benchmark others are compared to?",
  "Do consumers default to you for gifting in your category?",
  "Is discovery happening via search, social, retail, or WOM?",
  "Are you considered for both planned and impulse purchases?",
  "Is consideration rank consistent across segments?",
  "Do you track consideration set changes quarterly?",
  "Has your brand entered consideration in adjacent categories?"
];
QB.margin = [
  "Is your gross margin above the category benchmark?",
  "Has gross margin improved or declined over 3 years?",
  "Do you know gross margin for each individual SKU?",
  "Are your COGS optimized without compromising quality?",
  "Is your margin protected as input costs rise?",
  "Are you passing cost increases to consumers or absorbing?",
  "Do you have alternative suppliers to prevent margin compression?",
  "Is your sourcing cost competitive with the market?",
  "Have you identified products that drag down overall margin?",
  "Is pricing regularly reviewed against cost structure changes?",
  "Do you track contribution margin per channel?",
  "Is D2C margin higher than wholesale margin?",
  "Are there SKUs that should be discontinued for margin health?",
  "Is packaging and logistics cost optimized?",
  "Do you benchmark margins against competitor data?",
  "Has margin been impacted by promotional activity?",
  "Is your margin structure sustainable at 2x volume?",
  "Do you have a clear margin improvement roadmap?",
  "Are you investing in operational efficiency to protect margins?",
  "Is your mix shifting toward higher or lower margin products?",
  "Do you track gross margin per customer segment?",
  "Is margin healthy enough to fund brand-building?",
  "Are returns and wastage eating significantly into margin?",
  "Do you have full landed cost visibility across all SKUs?",
  "Is margin trajectory aligned with long-term profitability targets?"
];
QB.cac_ltv = [
  "Do you know your customer acquisition cost precisely?",
  "Is your CAC to LTV ratio above 3 to 1?",
  "Has CAC increased or decreased over the past year?",
  "Do you track CAC by acquisition channel?",
  "Which channel delivers lowest CAC with highest quality?",
  "Is CAC sustainable relative to average order value?",
  "Do you know LTV of your average customer?",
  "Is LTV based on actual repeat data, not projections?",
  "Are you investing more in retention vs acquisition?",
  "Can you identify which segments have highest LTV?",
  "Is CAC payback period less than 6 months?",
  "Do you have cohort analysis showing LTV trends?",
  "Are referral customers cheaper to acquire than paid?",
  "Is organic acquisition growing (reducing blended CAC)?",
  "Do you track full acquisition cost including overhead?",
  "Is CAC competitive vs industry benchmarks?",
  "Are there channels to stop due to high CAC?",
  "Do high-LTV customers show identifiable traits at acquisition?",
  "Is LTV growing as you improve product and experience?",
  "Are you measuring true incremental LTV?",
  "Do you have a strategy to increase average order value?",
  "Is cross-selling contributing meaningfully to LTV?",
  "Can you predict which new customers become high-LTV?",
  "Is your repeat purchase model optimized for LTV?",
  "Does customer onboarding improve retention and LTV?"
];
QB.channel = [
  "Do you know profitability of each sales channel?",
  "Is channel mix optimized for margin, not just revenue?",
  "Are you over-dependent on any single channel?",
  "Is D2C growing as a % of total revenue?",
  "Do marketplaces deliver acceptable margins after fees?",
  "Is modern trade margin healthy after listing fees?",
  "Do you have a clear strategy with roles per channel?",
  "Are channels cannibalizing each other?",
  "Is your fastest-growing channel also most profitable?",
  "Do you track return rates and service costs per channel?",
  "Is offline distribution efficient across target geography?",
  "Are you present where target consumers actually shop?",
  "Do you have exclusive channel partnerships?",
  "Is e-commerce profitable after ads and fulfillment?",
  "Are you investing in quick commerce and social commerce?",
  "Do you regularly prune underperforming channels?",
  "Is channel partner margin fair enough to incentivize?",
  "Are you leveraging data from all channels?",
  "Is inventory optimized across channels?",
  "Do you have a unified customer view across channels?",
  "Is pricing consistent or strategically differentiated by channel?",
  "Are you exploring B2B, export, or institutional channels?",
  "Do channel partners view your brand as a priority?",
  "Is supply chain designed to serve all channels efficiently?",
  "Have you mapped full P&L per channel including hidden costs?"
];
QB.rev_conc = [
  "Does any single product contribute more than 40% of revenue?",
  "Does any single customer contribute more than 20%?",
  "Does any single channel contribute more than 50%?",
  "Does any single geography contribute more than 60%?",
  "What happens if your top product is discontinued?",
  "Is revenue diversified across at least 3 product lines?",
  "Are you actively developing new revenue streams?",
  "Is seasonal revenue concentration manageable?",
  "Do you have contractual or recurring revenue?",
  "Is revenue mix shifting toward diversification?",
  "Have you stress-tested revenue if one channel is disrupted?",
  "Is your customer base diversified across segments?",
  "Do you have revenue from both B2C and B2B?",
  "Is export/multi-geography revenue growing?",
  "Are you launching at different price points for diversification?",
  "Is brand licensing generating alternative revenue?",
  "Do you have a pipeline of new products for future revenue?",
  "Is revenue from products launched in last 2 years above 15%?",
  "Are you vulnerable to regulatory changes on concentrated sources?",
  "Is your diversification plan documented and active?",
  "Do you have multiple suppliers to prevent supply-side risk?",
  "Is growth from existing products or new introductions?",
  "Are you exploring service-based revenue alongside products?",
  "Is your pricing model diversified?",
  "Would losing your 2nd largest revenue source be survivable?"
];
QB.brand_val = [
  "What % of revenue is from brand equity vs commodity value?",
  "Can you charge a premium purely because of your brand name?",
  "Have you quantified the financial value of your brand?",
  "Do brand-aware customers convert at a higher rate?",
  "Is your brand reducing customer acquisition cost?",
  "Does your brand attract better talent and reduce hiring costs?",
  "Is brand equity enabling favorable supplier/partner terms?",
  "Would a generic version of your product sell for much less?",
  "Is your brand commanding premium shelf space?",
  "Does your brand drive organic traffic you don't pay for?",
  "Is brand intangible value reflected in company valuation?",
  "Do investors cite your brand as a key asset?",
  "Is your brand enabling line extensions into new categories?",
  "Does brand premium increase with customer tenure?",
  "Are customers willing to wait rather than substitute?",
  "Is your brand contributing to higher LTV?",
  "Do trade partners give preferential terms for brand strength?",
  "Is your brand name a factor in partnership opportunities?",
  "Would competitors need 2x+ spend to match your recognition?",
  "Is brand equity appreciated and protected by leadership?",
  "Do you invest a defined % of revenue in brand building?",
  "Is your brand resilient to competitive pricing pressure?",
  "Has brand premium grown over the past 3 years?",
  "Do consumers associate positive emotions with your brand?",
  "Is your brand strong enough to survive a quality issue?"
];
QB.nps = [
  "Do you actively measure NPS on a regular basis?",
  "Is your NPS above the benchmark for your category?",
  "Has NPS trended upward over the past 4 quarters?",
  "Do you segment NPS by customer type, channel, geography?",
  "Do you follow up with detractors to understand complaints?",
  "What % of customers are Promoters (9-10)?",
  "Is your detractor percentage below 15%?",
  "Do you act on NPS feedback within 48 hours?",
  "Is there a closed-loop between NPS and improvements?",
  "Do Promoters actually refer new customers measurably?",
  "Is NPS tracked at specific touchpoints?",
  "Do team members have NPS targets in their KPIs?",
  "Is NPS consistent across different product lines?",
  "Do you benchmark NPS against direct competitors?",
  "Has any operational change measurably improved NPS?",
  "Do you survey a statistically significant sample?",
  "Is your NPS methodology consistent over time?",
  "Are NPS verbatims reviewed by leadership monthly?",
  "Do you track Relationship NPS vs Transactional NPS?",
  "Is NPS higher for repeat customers vs first-time?",
  "Do you publish NPS internally to drive accountability?",
  "Is Customer Effort Score tracked alongside NPS?",
  "Do high-NPS customers have higher lifetime value?",
  "Does NPS improvement correlate with revenue growth?",
  "Would your NPS withstand a price increase?"
];
QB.touchpoint = [
  "Is visual identity applied consistently across all touchpoints?",
  "Does website, packaging, social, and retail feel like one brand?",
  "Do you have comprehensive brand guidelines teams follow?",
  "Is brand tone of voice consistent across channels?",
  "Do third-party resellers maintain your brand standards?",
  "Is brand experience consistent online and offline?",
  "Do you audit touchpoint consistency at least quarterly?",
  "Are customer communications (email, SMS) on-brand?",
  "Is brand imagery and photography style consistent?",
  "Do all team members understand your brand promise?",
  "Is your brand consistent across markets and geographies?",
  "Are ad creatives consistent with packaging design language?",
  "Do service interactions reflect your brand personality?",
  "Is brand experience consistent peak and off-peak?",
  "Do you have a brand asset management system?",
  "Is brand presentation consistent across marketplaces?",
  "Do co-branded materials maintain your standards?",
  "Is physical touchpoint quality consistent?",
  "Do automated communications feel on-brand?",
  "Is your brand consistent in complaints and returns?",
  "Do you track and address reported inconsistencies?",
  "Is employer brand consistent with consumer brand?",
  "Are brand guidelines accessible to all stakeholders?",
  "Is brand consistency measurable via audit scores?",
  "Do customers describe a consistent experience?"
];
QB.digital = [
  "Is website loading speed under 3 seconds on mobile?",
  "Is your website mobile-optimized and responsive?",
  "Is checkout frictionless (under 3 steps)?",
  "Is website design aligned with brand identity?",
  "Is your mobile app experience consistent with your brand?",
  "Is website UI intuitive for first-time visitors?",
  "Do you track and optimize bounce rate and time-on-site?",
  "Is search functionality effective?",
  "Are product pages informative and conversion-optimized?",
  "Is your website accessible to users with disabilities?",
  "Do you A/B test digital experiences regularly?",
  "Is email experience well-designed and mobile-optimized?",
  "Is digital customer service responsive and helpful?",
  "Do you have progressive personalization?",
  "Is post-purchase digital experience seamless?",
  "Do you fix broken links and technical issues promptly?",
  "Is your website SEO-optimized for discovery?",
  "Do you track user journeys and identify drop-offs?",
  "Is digital experience consistent across browsers/devices?",
  "Do you have a digital experience improvement roadmap?",
  "Is digital content regularly updated and fresh?",
  "Are social media profiles optimized and managed?",
  "Do digital touchpoints integrate seamlessly?",
  "Is digital experience competitive with category leaders?",
  "Do customers rate your digital experience positively?"
];
QB.service = [
  "Is average service response time under 4 hours?",
  "What is your first-contact resolution rate?",
  "Do you offer service on preferred customer channels?",
  "Is your CSAT above industry average?",
  "Do service interactions reflect your brand values?",
  "Is service quality consistent across channels?",
  "Do you have clear SLAs for response and resolution?",
  "Are complaints tracked and used for improvement?",
  "Is your service team trained on brand voice?",
  "Do you proactively reach out to customers with issues?",
  "Is your returns process simple and customer-friendly?",
  "Do you follow up after resolution to confirm satisfaction?",
  "Is self-service (FAQ, help center) comprehensive?",
  "Do you track customer effort score for service?",
  "Is escalation handled without customers repeating issues?",
  "Do you monitor social media for service complaints?",
  "Is your service team empowered to resolve without approvals?",
  "Do you have a service recovery process for major failures?",
  "Is service a competitive differentiator?",
  "Do you measure service metrics weekly?",
  "Is service feedback fed back to product/operations?",
  "Are peak-period service resources adequate?",
  "Do you survey after every service interaction?",
  "Is your service team motivated and well-compensated?",
  "Would customers cite service as a reason to recommend?"
];
QB.retail = [
  "Is packaging design distinctive and recognizable on shelf?",
  "Does in-store presence reflect your brand identity?",
  "Is product display consistent across retail locations?",
  "Do you have planogram guidelines retailers follow?",
  "Is unboxing designed to create a memorable first impression?",
  "Do you invest in POS materials that enhance shopping?",
  "Is packaging functional beyond aesthetics?",
  "Do you conduct retail audits for brand standards?",
  "Is your sampling/trial experience well-executed?",
  "Do you have a branded retail or popup presence?",
  "Is packaging sustainable and communicated effectively?",
  "Do you track shelf visibility and share of shelf?",
  "Is physical touchpoint quality consistent with positioning?",
  "Do retail staff understand your brand story?",
  "Are in-store promotions on-brand, not purely discount-driven?",
  "Is packaging designed for the specific retail format?",
  "Do you gather feedback on the physical buying experience?",
  "Is marketplace product presentation visually compelling?",
  "Do you have branded delivery packaging for D2C?",
  "Is retail experience differentiated from competitors?",
  "Do you leverage sensory elements in physical experiences?",
  "Is event marketing well-executed?",
  "Do you have a signature moment in the physical experience?",
  "Is physical and digital retail integrated (omnichannel)?",
  "Do customers share photos of your packaging on social?"
];
QB.rev_growth = [
  "Is year-over-year revenue growth positive?",
  "Is growth rate above category average?",
  "Has growth accelerated or decelerated over 3 years?",
  "Is growth driven by volume, price, or mix?",
  "Are you growing in core market, not just new markets?",
  "Is growth sustainable without increasing promos?",
  "Do you have a clear growth target and plan?",
  "Is quarterly growth consistent or highly variable?",
  "Are you growing faster than closest competitors?",
  "Is growth compounding year over year?",
  "Is growth capital-efficient?",
  "Are you growing profitably (margin maintained)?",
  "Do you have visibility into growth for next 12 months?",
  "Is growth from repeat purchases, not just new acquisition?",
  "Are you meeting internal growth projections?",
  "Is growth creating operational strain to address?",
  "Do you benchmark against competitors and disruptors?",
  "Is growth diversified across multiple products?",
  "Are you reinvesting appropriately into growth?",
  "Is brand growth outpacing distribution (pull demand)?",
  "Do you track same-store vs new account growth?",
  "Is growth reflected in market share gains?",
  "Are you tracking leading growth indicators?",
  "Is growth aligned with long-term brand vision?",
  "Would your growth rate attract investor interest?"
];
QB.mkt_expand = [
  "Are you actively expanding into new geographic markets?",
  "Do you have a clear expansion strategy with prioritized targets?",
  "Have you entered at least one new market in 2 years?",
  "Is your expansion methodology proven and repeatable?",
  "Do you adapt your brand or use a standardized approach?",
  "Is expansion profitable within 12 months of entry?",
  "Are you expanding into markets with strong growth potential?",
  "Do you have supply chain infrastructure for expansion?",
  "Is awareness building ahead of distribution in new markets?",
  "Are you using a phased approach (test then scale)?",
  "Do you have local partnerships in target markets?",
  "Is digital enabling expansion without physical infrastructure?",
  "Are you expanding into adjacent categories too?",
  "Do you track expansion performance separately?",
  "Is expansion creating meaningful incremental revenue?",
  "Have you validated demand before committing resources?",
  "Are regulatory requirements mapped for target markets?",
  "Is expansion adequately funded?",
  "Do you have exit strategy if targets are not met?",
  "Are you using e-commerce to test before physical entry?",
  "Is expansion pace appropriate for operational capacity?",
  "Do you analyze competitive dynamics in each market?",
  "Is positioning transferable or needs localization?",
  "Are you building awareness ahead of product availability?",
  "Do you track ROI of expansion efforts?"
];
QB.new_cust = [
  "Is new customer acquisition rate growing month-over-month?",
  "Do you know exact new customers acquired each month?",
  "Are you acquiring from intended target segments?",
  "Is acquisition diversified across multiple channels?",
  "Is trial rate healthy for your category?",
  "Do you track quality of new customers, not just quantity?",
  "Is acquisition cost trending downward?",
  "Are you acquiring customers competitors are losing?",
  "Is organic acquisition (non-paid) growing?",
  "Do you have a strategy beyond running ads?",
  "Is referral driving meaningful new customer volume?",
  "Are new customers discovering through content and brand?",
  "Do you track awareness-to-first-purchase conversion?",
  "Is trial-to-repeat conversion healthy?",
  "Are you penetrating new demographics?",
  "Is database growing to support growth targets?",
  "Do you have an acquisition funnel with conversion rates?",
  "Are you acquiring at right price points (not just discounters)?",
  "Is your brand attracting new category entrants?",
  "Do you track how new customers heard about you?",
  "Is your sampling program effective at converting?",
  "Are trade promos bringing new or subsidizing existing customers?",
  "Is acquisition velocity sufficient for revenue targets?",
  "Do you track acquisition against category penetration data?",
  "Do you have a clear ideal customer profile guiding targeting?"
];
QB.innovation = [
  "Have you launched a new product in the last 12 months?",
  "What % of revenue from products launched in last 2 years?",
  "Do you have a formal innovation pipeline with stages?",
  "Is innovation driven by consumer insights, not just ideas?",
  "Have recent launches met business case targets?",
  "Do you allocate a defined budget for R&D?",
  "Is innovation pace faster than primary competitors?",
  "Do you kill underperforming innovations quickly?",
  "Is innovation expanding your addressable market?",
  "Do you test concepts with consumers before launch?",
  "Is your innovation team adequately resourced?",
  "Do you track success rates and learn from failures?",
  "Are you innovating on business model, not just product?",
  "Is innovation balanced between incremental and breakthrough?",
  "Do consumers perceive your brand as innovative?",
  "Are you leveraging tech trends in product innovation?",
  "Is packaging innovation keeping pace with product?",
  "Do you have innovation partnerships?",
  "Is innovation fast enough to respond to market changes?",
  "Are innovations protectable (patents, moats)?",
  "Do new products strengthen or dilute positioning?",
  "Is innovation pipeline visible to leadership?",
  "Are you innovating for future needs, not just current?",
  "Is time-to-market competitive?",
  "Do innovations create meaningful competitive advantages?"
];
QB.retention = [
  "Do you know your retention rate precisely?",
  "Is retention above your category benchmark?",
  "Has retention improved over the past year?",
  "Do you track retention by customer segment?",
  "Do you have a proactive retention strategy?",
  "Is repeat purchase rate growing?",
  "Do you track reasons customers leave?",
  "Is churn rate below category average?",
  "Do you have an early warning system for churn risk?",
  "Is retention improving as you enhance experience?",
  "Do retained customers increase spend over time?",
  "Is your loyalty program driving measurable retention?",
  "Do you segment retention strategies by customer value?",
  "Is post-purchase experience designed for repeat buying?",
  "Do you track time between purchases and optimize frequency?",
  "Are content-engaged customers more retained?",
  "Is auto-replenishment or subscription model effective?",
  "Do you address the top 3 reasons for customer loss?",
  "Is communication cadence optimized for retention?",
  "Do you personalize retention based on behavior?",
  "Is product quality consistent enough to prevent churn?",
  "Do you re-engage lapsed customers with campaigns?",
  "Is retention better for organic vs paid acquired customers?",
  "Do you have a reactivation strategy for dormant customers?",
  "Is retention contributing to growing LTV?"
];
QB.dig_mat = [
  "Is your core business process digitized end-to-end?",
  "Do you have a modern tech stack that supports growth?",
  "Can your e-commerce handle 3x current traffic?",
  "Do you have a CRM capturing and activating customer data?",
  "Is inventory management real-time and integrated?",
  "Do you use marketing automation for communications?",
  "Is data analytics sufficient for data-driven decisions?",
  "Do you have APIs connecting key business tools?",
  "Is digital infrastructure secure and compliant?",
  "Do you have a digital transformation roadmap?",
  "Is your team digitally literate?",
  "Do you use cloud-based collaborative tools?",
  "Is your supply chain digitized with real-time visibility?",
  "Do you have a mobile-first customer approach?",
  "Is digital advertising capability sophisticated?",
  "Do you have social commerce capabilities?",
  "Is customer data unified across channels?",
  "Do you leverage digital feedback collection tools?",
  "Is reporting and dashboarding real-time?",
  "Do you invest a defined % in technology?",
  "Is your ERP modern and scalable?",
  "Do you use digital project management tools?",
  "Is digital capability a competitive advantage?",
  "Do you benchmark against digitally-native competitors?",
  "Are you prepared for the next platform shift?"
];
QB.ai_ready = [
  "Are you currently using AI in any business area?",
  "Do you have first-party data that could improve AI models?",
  "Is customer data clean, structured, and accessible?",
  "Do you use AI for content, insights, or personalization?",
  "Is your team trained on relevant AI tools?",
  "Do you have a data governance framework?",
  "Are you using predictive analytics for forecasting?",
  "Is customer segmentation powered by data science?",
  "Do you use AI for ad optimization and bidding?",
  "Is your customer service using AI effectively?",
  "Do you have an AI strategy for the next 12 months?",
  "Is data infrastructure ready for ML workloads?",
  "Are you using AI for competitive intelligence?",
  "Do you experiment with generative AI for content?",
  "Is your recommendation engine AI-powered?",
  "Do you track AI-related ROI and efficiency gains?",
  "Is leadership educated on AI opportunities and risks?",
  "Are you collecting data that will be valuable for future AI?",
  "Do you use AI for dynamic pricing or promo optimization?",
  "Is your brand voice encoded for AI content generation?",
  "Are you aware of AI disruption risks in your category?",
  "Do you have AI-ready talent in team or partners?",
  "Is journey data captured for AI personalization?",
  "Are you piloting AI with clear success metrics?",
  "Do you have a data ethics policy for AI implementation?"
];
QB.talent = [
  "Is employer brand strong enough to attract top talent?",
  "Do you have a defined employer value proposition?",
  "Is team retention above industry average?",
  "Do candidates proactively apply or do you rely on recruiting?",
  "Are your employer review ratings above 3.5 stars?",
  "Is leadership team stable (low C-suite turnover)?",
  "Do you invest in employee development and training?",
  "Is your team culturally aligned with brand values?",
  "Do you have succession plans for key positions?",
  "Is compensation competitive for your market?",
  "Do employees recommend your company as a great workplace?",
  "Is your team diverse in skills and perspectives?",
  "Do you have structured onboarding for new hires?",
  "Is organizational structure designed for agility?",
  "Do you hire for capability gaps in your growth plan?",
  "Is your team equipped with skills for the next 3 years?",
  "Do employees believe in your brand mission?",
  "Is employee engagement above benchmark?",
  "Do you have a learning budget per employee?",
  "Is recruitment efficient (time to fill key roles)?",
  "Do you track and improve employee satisfaction?",
  "Are employees active brand ambassadors on social?",
  "Is team structure scalable for 2x growth?",
  "Do you have a freelancer network to supplement team?",
  "Is leadership visible, accessible, and inspiring?"
];
QB.sustain = [
  "Do you have a defined sustainability or ESG strategy?",
  "Is product packaging environmentally sustainable?",
  "Do consumers care about sustainability in your category?",
  "Have you communicated your sustainability position?",
  "Is supply chain audited for ethical practices?",
  "Do you measure and report environmental footprint?",
  "Are you compliant with sustainability regulations?",
  "Is sustainability part of brand story, not just compliance?",
  "Do you have relevant sustainability certifications?",
  "Is sustainability positioning authentic and backed by action?",
  "Will consumers pay more for your sustainable practices?",
  "Do you track and reduce operational waste?",
  "Is sustainability a competitive advantage in your category?",
  "Are you prepared for increased reporting requirements?",
  "Do employees support your sustainability goals?",
  "Is sustainability investment creating business benefits?",
  "Are you avoiding greenwashing?",
  "Do you collaborate on industry sustainability standards?",
  "Is product lifecycle designed for sustainability?",
  "Do you use sustainability in marketing communications?",
  "Is sustainability in your product development process?",
  "Do you benchmark sustainability vs competitors?",
  "Are you targeting sustainability-conscious segments?",
  "Is sustainability performance improving year over year?",
  "Do investors evaluate your sustainability credentials?"
];
QB.adapt = [
  "Can you make a major strategic decision within 2 weeks?",
  "Do you have a culture of testing and experimentation?",
  "Have you successfully pivoted in the last 2 years?",
  "Is decision-making fast and decentralized enough?",
  "Do you regularly update strategy based on market feedback?",
  "Is your team comfortable with ambiguity and change?",
  "Do you allocate resources to emerging opportunities quickly?",
  "Is organizational structure flat for fast information flow?",
  "Do you track competitive moves in real-time?",
  "Can you launch a product or campaign in under 60 days?",
  "Is supply chain flexible for demand changes?",
  "Do you test new ideas before fully committing?",
  "Is failure treated as a learning opportunity?",
  "Do you have scenario plans for major disruptions?",
  "Can you shift marketing spend between channels in a week?",
  "Do you use data to drive decisions vs gut instinct?",
  "Is your tech stack modular and adaptable?",
  "Do you have financial flexibility for unexpected opportunities?",
  "Is leadership proactive about change?",
  "Do you conduct post-mortems and implement learnings?",
  "Is positioning flexible enough to evolve without losing identity?",
  "Do you adopt best practices from outside your category?",
  "Is your team empowered to decide without excessive approvals?",
  "Do you run innovation sprints or hackathons?",
  "Would your organization thrive or struggle if disrupted?"
];

// Count total questions
var TOTAL_Q = 0;
DIMS.forEach(function(d) {
  d.ms.forEach(function(m) {
    TOTAL_Q += (QB[m.id] || []).length;
  });
});

// ═══ STORAGE ═══
async function loadData(key, fallback) {
  try {
    var r = await window.storage.get(key);
    return r ? JSON.parse(r.value) : fallback;
  } catch(e) {
    return fallback;
  }
}
async function saveData(key, val) {
  try {
    await window.storage.set(key, JSON.stringify(val));
  } catch(e) {
    console.error(e);
  }
}

// ═══ SMALL COMPONENTS ═══
function Header(props) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, margin: 0, color: C.t1 }}>{props.title}</h1>
      {props.sub && <p style={{ fontSize: 11, color: C.t2, marginTop: 4 }}>{props.sub}</p>}
      <div style={{ width: 40, height: 2, background: C.gd, marginTop: 10 }} />
    </div>
  );
}

function Empty(props) {
  return (
    <div style={{ background: C.cd, border: "1px solid " + C.bd, borderRadius: 10, padding: 40, textAlign: "center" }}>
      <div style={{ fontSize: 12, color: C.t3, marginBottom: props.onAction ? 12 : 0 }}>{props.msg}</div>
      {props.onAction && <button onClick={props.onAction} style={{ background: C.gd, color: C.bg, border: "none", borderRadius: 6, padding: "10px 20px", fontFamily: "monospace", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{props.label}</button>}
    </div>
  );
}

function FieldInput(props) {
  var st = { background: C.el, border: "1px solid " + C.bd, borderRadius: 6, padding: "8px 12px", color: C.t1, fontSize: 13, width: "100%", fontFamily: "'DM Sans',sans-serif" };
  return (
    <div>
      <label style={{ fontFamily: "monospace", fontSize: 8, color: C.gd, letterSpacing: 1, display: "block", marginBottom: 4 }}>{props.label}</label>
      {props.multi
        ? <textarea rows={2} value={props.value} onChange={function(e) { props.onChange(e.target.value); }} placeholder={props.placeholder} style={Object.assign({}, st, { resize: "vertical" })} />
        : <input value={props.value} onChange={function(e) { props.onChange(e.target.value); }} placeholder={props.placeholder} style={st} />
      }
    </div>
  );
}

// ═══ MAIN APP ═══
export default function BrandOS() {
  var _s = useState([]);
  var clients = _s[0], setClients = _s[1];
  var _v = useState("dash");
  var view = _v[0], setView = _v[1];
  var _a = useState(null);
  var ac = _a[0], setAc = _a[1];
  var _l = useState(true);
  var loading = _l[0], setLoading = _l[1];
  var _ai = useState("");
  var aiOut = _ai[0], setAiOut = _ai[1];
  var _al = useState(false);
  var aiLoading = _al[0], setAiLoading = _al[1];
  var _d = useState(0);
  var aDim = _d[0], setADim = _d[1];
  var _m = useState(0);
  var aMet = _m[0], setAMet = _m[1];

  useEffect(function() {
    loadData("bos4-cl", []).then(function(c) { setClients(c); setLoading(false); });
  }, []);

  function persist(updated) {
    setClients(updated);
    saveData("bos4-cl", updated);
  }

  function addClient(f) {
    var c = Object.assign({}, f, { id: Date.now().toString(), ans: {}, ts: new Date().toISOString() });
    var updated = clients.concat([c]);
    persist(updated);
    setAc(c);
    setView("assess");
  }

  function setAnswer(cid, mid, qi, val) {
    var updated = clients.map(function(c) {
      if (c.id !== cid) return c;
      var ans = Object.assign({}, c.ans);
      if (!ans[mid]) ans[mid] = {};
      ans[mid] = Object.assign({}, ans[mid]);
      ans[mid][qi] = val;
      return Object.assign({}, c, { ans: ans });
    });
    persist(updated);
    setAc(updated.find(function(c) { return c.id === cid; }));
  }

  function mScore(client, mid) {
    if (!client || !client.ans || !client.ans[mid]) return 0;
    var a = client.ans[mid];
    var qs = QB[mid] || [];
    var sum = 0, cnt = 0;
    qs.forEach(function(_, i) {
      if (a[i] !== undefined) { sum += a[i]; cnt++; }
    });
    return cnt > 0 ? Math.round((sum / cnt / 5) * 100) : 0;
  }

  function dScore(client, dim) {
    var total = 0, wt = 0;
    dim.ms.forEach(function(m) {
      var s = mScore(client, m.id);
      if (s > 0) { total += s * m.wt; wt += m.wt; }
    });
    return wt > 0 ? Math.round(total / wt) : 0;
  }

  function composite(client) {
    var scores = DIMS.map(function(d) { return dScore(client, d); }).filter(function(s) { return s > 0; });
    return scores.length > 0 ? Math.round(scores.reduce(function(a, b) { return a + b; }, 0) / scores.length) : 0;
  }

  function totalAnswered(client) {
    if (!client || !client.ans) return 0;
    return Object.values(client.ans).reduce(function(t, a) { return t + Object.keys(a).length; }, 0);
  }

  function deleteClient(cid) {
    var updated = clients.filter(function(c) { return c.id !== cid; });
    persist(updated);
    setAc(null);
    setView("dash");
  }

  function genReport(client) {
    setAiLoading(true);
    setAiOut("");
    var ds = DIMS.map(function(d) { return d.nm + ": " + dScore(client, d) + "/100 (" + d.ms.map(function(m) { return m.nm + ":" + mScore(client, m.id); }).join(", ") + ")"; }).join("\n");
    var cs = composite(client);
    var prompt = "You are a senior brand strategist at BBP India. Generate a Brand Health Report.\n\nCLIENT: " + client.name + " | CATEGORY: " + client.category + " | WEBSITE: " + (client.website || "N/A") + " | REVENUE: " + (client.revenue || "N/A") + "\nCOMPETITORS: " + (client.competitors || "N/A") + " | CHALLENGES: " + (client.challenges || "N/A") + "\nCOMPOSITE: " + cs + "/100 (" + sTier(cs) + ")\n\n" + ds + "\n\nGenerate in markdown: Executive Summary (3 paragraphs), Dimension Analysis (each dimension with score, insights, recommendation), Top 5 Strategic Recommendations, Recommended BBP Engagement Tier. Confident consulting voice, specific, premium deliverable.";
    fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 4000, messages: [{ role: "user", content: prompt }] })
    }).then(function(r) { return r.json(); }).then(function(data) {
      var text = (data.content || []).map(function(b) { return b.text || ""; }).join("\n");
      setAiOut(text || "Report generation failed.");
    }).catch(function(e) {
      setAiOut("Error: " + e.message);
    }).finally(function() {
      setAiLoading(false);
    });
  }

  if (loading) {
    return <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: C.gd, fontFamily: "monospace" }}>Loading Brand OS...</span></div>;
  }

  var curDim = DIMS[aDim];
  var curMet = curDim.ms[aMet];
  var curQs = QB[curMet.id] || [];

  // ═══ RENDER ═══
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.bg, fontFamily: "'DM Sans',sans-serif", color: C.t1 }}>
      <style>{
        "@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;600&display=swap');" +
        "*{box-sizing:border-box;margin:0;padding:0}" +
        "::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:#333;border-radius:3px}" +
        "body{background:" + C.bg + "}" +
        "input,textarea{font-family:'DM Sans',sans-serif}input:focus,textarea:focus{outline:1px solid " + C.gd + "60}"
      }</style>

      {/* SIDEBAR */}
      <div style={{ width: 210, background: C.sf, borderRight: "1px solid " + C.bd, display: "flex", flexDirection: "column", flexShrink: 0, position: "sticky", top: 0, height: "100vh", overflowY: "auto" }}>
        <div style={{ padding: "20px 16px 12px" }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 900 }}>Brand OS<span style={{ color: C.gd }}>{"\u2122"}</span></div>
          <div style={{ fontFamily: "monospace", fontSize: 8, color: C.gd, letterSpacing: 2, marginTop: 2 }}>BBP INDIA | {TOTAL_Q} QUESTIONS</div>
        </div>
        <nav style={{ padding: "0 8px", flex: 1 }}>
          {[
            { id: "dash", l: "Dashboard" },
            { id: "new", l: "New Client" },
            { id: "assess", l: "Assessment" },
            { id: "results", l: "Results" },
            { id: "report", l: "AI Report" },
            { id: "preview", l: "Preview" }
          ].map(function(n) {
            return (
              <button key={n.id} onClick={function() { setView(n.id); }} style={{ width: "100%", textAlign: "left", padding: "8px 11px", marginBottom: 1, borderRadius: 6, border: "none", background: view === n.id ? C.gd + "15" : "transparent", color: view === n.id ? C.gd : C.t2, cursor: "pointer", fontSize: 12, fontWeight: 500, fontFamily: "'DM Sans',sans-serif" }}>
                {n.l}
              </button>
            );
          })}
        </nav>
        <div style={{ borderTop: "1px solid " + C.bd, padding: 8 }}>
          <div style={{ fontFamily: "monospace", fontSize: 8, color: C.t3, letterSpacing: 1, padding: "0 6px 5px" }}>CLIENTS ({clients.length})</div>
          {clients.map(function(c) {
            return (
              <button key={c.id} onClick={function() { setAc(c); setView("assess"); }} style={{ width: "100%", textAlign: "left", padding: "6px 9px", marginBottom: 1, borderRadius: 5, border: "none", background: ac && ac.id === c.id ? C.el : "transparent", color: ac && ac.id === c.id ? C.t1 : C.t3, cursor: "pointer", fontSize: 11 }}>
                <div style={{ fontWeight: 500 }}>{c.name}</div>
                <div style={{ fontSize: 9, color: C.t3 }}>{totalAnswered(c)}/{TOTAL_Q} | {composite(c) || "--"}/100</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>

        {/* DASHBOARD */}
        {view === "dash" && (
          <div>
            <Header title="Brand OS Dashboard" sub={clients.length + " clients | " + TOTAL_Q + " questions | 30 sub-metrics | 6 dimensions"} />
            {clients.length === 0 ? (
              <Empty msg="No clients yet. Add your first client to begin." onAction={function() { setView("new"); }} label="+ Add Client" />
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                {clients.map(function(c) {
                  var cs = composite(c);
                  var an = totalAnswered(c);
                  var pc = Math.round(an / TOTAL_Q * 100);
                  return (
                    <div key={c.id} onClick={function() { setAc(c); setView("assess"); }} style={{ background: C.cd, border: "1px solid " + C.bd, borderRadius: 10, padding: 18, cursor: "pointer" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                        <div>
                          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, margin: 0 }}>{c.name}</h3>
                          <div style={{ fontSize: 10, color: C.t3, marginTop: 2 }}>{c.category}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontFamily: "monospace", fontSize: 24, fontWeight: 700, color: cs > 0 ? sColor(cs) : C.t3 }}>{cs || "--"}</div>
                          <div style={{ fontFamily: "monospace", fontSize: 8, color: cs > 0 ? sColor(cs) : C.t3 }}>{cs > 0 ? sTier(cs).toUpperCase() : "PENDING"}</div>
                        </div>
                      </div>
                      <div style={{ background: C.el, borderRadius: 3, height: 5, marginBottom: 6, overflow: "hidden" }}>
                        <div style={{ width: pc + "%", height: "100%", background: C.gd, borderRadius: 3 }} />
                      </div>
                      <div style={{ fontSize: 9, color: C.t3, textAlign: "right" }}>{an}/{TOTAL_Q} answered ({pc}%)</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* NEW CLIENT */}
        {view === "new" && (
          <NewClientForm onSubmit={addClient} />
        )}

        {/* ASSESSMENT */}
        {view === "assess" && (
          ac ? (
            <div>
              <Header title={ac.name + " \u2014 Assessment"} sub={totalAnswered(ac) + "/" + TOTAL_Q + " answered | Score: " + (composite(ac) || "--") + "/100"} />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 16 }}>
                {DIMS.map(function(d, i) {
                  var s = dScore(ac, d);
                  return (
                    <button key={d.id} onClick={function() { setADim(i); setAMet(0); }} style={{ padding: "8px 12px", borderRadius: 7, border: "1px solid " + (aDim === i ? d.cl + "50" : C.bd), background: aDim === i ? d.cl + "12" : C.cd, color: aDim === i ? d.cl : C.t2, cursor: "pointer", fontSize: 11, fontWeight: 500, fontFamily: "'DM Sans',sans-serif" }}>
                      <span>{d.ic} {d.sh} </span>
                      <span style={{ fontFamily: "monospace", fontWeight: 700, color: s > 0 ? sColor(s) : C.t3 }}>{s || "--"}</span>
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 16 }}>
                {curDim.ms.map(function(m, i) {
                  var s = mScore(ac, m.id);
                  return (
                    <button key={m.id} onClick={function() { setAMet(i); }} style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid " + (aMet === i ? curDim.cl + "40" : C.bd), background: aMet === i ? C.el : C.cd, color: aMet === i ? C.t1 : C.t3, cursor: "pointer", fontSize: 11, fontFamily: "'DM Sans',sans-serif", fontWeight: 500 }}>
                      {m.nm} <span style={{ fontFamily: "monospace", color: s > 0 ? sColor(s) : C.t3, marginLeft: 4 }}>{s || "--"}</span>
                    </button>
                  );
                })}
              </div>
              <div style={{ background: C.cd, border: "1px solid " + curDim.cl + "20", borderRadius: 10, padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 18 }}>{curDim.ic}</span>
                  <div>
                    <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 17, margin: 0 }}>{curMet.nm}</h3>
                    <div style={{ fontSize: 10, color: C.t3 }}>Weight: {curMet.wt}%</div>
                  </div>
                  <div style={{ marginLeft: "auto", fontFamily: "monospace", fontSize: 22, fontWeight: 700, color: mScore(ac, curMet.id) > 0 ? sColor(mScore(ac, curMet.id)) : C.t3 }}>
                    {mScore(ac, curMet.id) || "--"}<span style={{ fontSize: 10, color: C.t3 }}>/100</span>
                  </div>
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  {curQs.map(function(q, qi) {
                    var val = ac.ans && ac.ans[curMet.id] ? ac.ans[curMet.id][qi] : undefined;
                    return (
                      <div key={qi} style={{ background: C.el, borderRadius: 7, padding: "12px 14px", borderLeft: "3px solid " + (val !== undefined ? curDim.cl + "60" : C.bd) }}>
                        <div style={{ fontSize: 12, color: C.t1, marginBottom: 8, lineHeight: 1.5 }}>
                          <span style={{ fontFamily: "monospace", fontSize: 9, color: curDim.cl, marginRight: 6 }}>Q{qi + 1}</span>
                          {q}
                        </div>
                        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                          <span style={{ fontSize: 9, color: C.t3, width: 55 }}>Strongly No</span>
                          {[1, 2, 3, 4, 5].map(function(n) {
                            return (
                              <button key={n} onClick={function() { setAnswer(ac.id, curMet.id, qi, n); }} style={{ width: 36, height: 32, borderRadius: 5, border: "1px solid " + (val === n ? curDim.cl : C.bd), background: val === n ? curDim.cl + "25" : C.bg, color: val === n ? curDim.cl : C.t3, cursor: "pointer", fontFamily: "monospace", fontSize: 13, fontWeight: val === n ? 700 : 400 }}>
                                {n}
                              </button>
                            );
                          })}
                          <span style={{ fontSize: 9, color: C.t3, width: 55, textAlign: "right" }}>Strongly Yes</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : <Empty msg="Select a client from the sidebar." />
        )}

        {/* RESULTS */}
        {view === "results" && (
          ac ? (
            <div>
              <Header title={ac.name + " \u2014 Results"} sub={totalAnswered(ac) + "/" + TOTAL_Q + " answered"} />
              <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 16, marginBottom: 20 }}>
                <div style={{ background: C.cd, border: "1px solid " + C.bd, borderRadius: 10, padding: 20, textAlign: "center" }}>
                  <div style={{ fontFamily: "monospace", fontSize: 8, color: C.gd, letterSpacing: 2, marginBottom: 6 }}>COMPOSITE</div>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 52, fontWeight: 900, color: composite(ac) > 0 ? sColor(composite(ac)) : C.t3, lineHeight: 1 }}>{composite(ac) || "--"}</div>
                  <div style={{ fontFamily: "monospace", fontSize: 9, color: composite(ac) > 0 ? sColor(composite(ac)) : C.t3, marginTop: 6 }}>{composite(ac) > 0 ? sTier(composite(ac)).toUpperCase() : "INCOMPLETE"}</div>
                </div>
                <div style={{ background: C.cd, border: "1px solid " + C.bd, borderRadius: 10, padding: 16 }}>
                  <div style={{ display: "grid", gap: 8 }}>
                    {DIMS.map(function(d) {
                      var s = dScore(ac, d);
                      return (
                        <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 14 }}>{d.ic}</span>
                          <span style={{ fontFamily: "monospace", fontSize: 10, color: C.t3, width: 60 }}>{d.sh}</span>
                          <div style={{ flex: 1, background: C.el, borderRadius: 3, height: 14, overflow: "hidden" }}>
                            <div style={{ width: s + "%", height: "100%", background: d.cl, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: s > 0 ? sColor(s) : C.t3, width: 30, textAlign: "right" }}>{s || "--"}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              {DIMS.map(function(d) {
                var ds = dScore(ac, d);
                return (
                  <div key={d.id} style={{ background: C.cd, border: "1px solid " + C.bd, borderRadius: 10, padding: 18, marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                      <span style={{ fontSize: 16 }}>{d.ic}</span>
                      <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, margin: 0 }}>{d.nm}</h3>
                      <span style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 700, color: ds > 0 ? sColor(ds) : C.t3, marginLeft: "auto" }}>{ds || "--"}/100</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                      {d.ms.map(function(m) {
                        var ms = mScore(ac, m.id);
                        return (
                          <div key={m.id} style={{ background: C.el, borderRadius: 7, padding: 12 }}>
                            <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 700, color: ms > 0 ? sColor(ms) : C.t3, marginBottom: 1 }}>{ms || "--"}</div>
                            <div style={{ fontSize: 10, color: C.t1, fontWeight: 500 }}>{m.nm}</div>
                            <div style={{ fontSize: 8, color: C.t3, marginTop: 2 }}>wt {m.wt}%</div>
                            <div style={{ background: C.bg, borderRadius: 2, height: 3, marginTop: 5, overflow: "hidden" }}>
                              <div style={{ width: ms + "%", height: "100%", background: d.cl, borderRadius: 2 }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <Empty msg="Select a client to view results." />
        )}

        {/* AI REPORT */}
        {view === "report" && (
          <div>
            <Header title="AI Report Generator" sub="Generates a full Brand Health Report from assessment data" />
            {!ac ? <Empty msg="Select a client first." /> : (
              <div>
                <div style={{ display: "flex", gap: 12, marginBottom: 18, alignItems: "center" }}>
                  <div style={{ background: C.cd, border: "1px solid " + C.bd, borderRadius: 8, padding: "10px 16px", flex: 1 }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{ac.name}</span>
                    <span style={{ fontSize: 11, color: C.t3, marginLeft: 10 }}>Score: {composite(ac)}/100</span>
                  </div>
                  <button onClick={function() { genReport(ac); }} disabled={aiLoading} style={{ background: C.gd, color: C.bg, border: "none", borderRadius: 7, padding: "11px 22px", fontFamily: "monospace", fontSize: 11, fontWeight: 600, cursor: aiLoading ? "wait" : "pointer", opacity: aiLoading ? 0.6 : 1 }}>
                    {aiLoading ? "GENERATING..." : "GENERATE REPORT"}
                  </button>
                </div>
                {aiOut && (
                  <div style={{ background: C.cd, border: "1px solid " + C.bd, borderRadius: 10, padding: 24 }}>
                    <div style={{ fontFamily: "monospace", fontSize: 8, color: C.gd, letterSpacing: 1.5, marginBottom: 12 }}>AI-GENERATED BRAND HEALTH REPORT</div>
                    <div style={{ fontSize: 13, color: C.t2, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
                      {aiOut.split("\n").map(function(line, i) {
                        if (line.indexOf("## ") === 0) return <h2 key={i} style={{ fontFamily: "'Playfair Display',serif", fontSize: 19, color: C.t1, margin: "22px 0 8px", borderBottom: "1px solid " + C.bd, paddingBottom: 6 }}>{line.replace("## ", "")}</h2>;
                        if (line.indexOf("### ") === 0) return <h3 key={i} style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, color: C.gd, margin: "16px 0 5px" }}>{line.replace("### ", "")}</h3>;
                        if (line.indexOf("- ") === 0) return <div key={i} style={{ paddingLeft: 12, marginBottom: 4, borderLeft: "2px solid " + C.bd }}>{line.replace("- ", "")}</div>;
                        if (line.trim() === "") return <div key={i} style={{ height: 5 }} />;
                        return <p key={i} style={{ margin: "0 0 5px" }}>{line}</p>;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* PREVIEW */}
        {view === "preview" && (
          ac ? (
            <PreviewView client={ac} composite={composite} dScore={dScore} />
          ) : <Empty msg="Select a client for preview." />
        )}

      </div>
    </div>
  );
}

// ═══ NEW CLIENT FORM ═══
function NewClientForm(props) {
  var _f = useState({ name: "", category: "", website: "", revenue: "", competitors: "", challenges: "" });
  var form = _f[0], setForm = _f[1];
  function set(k, v) { setForm(function(p) { var n = Object.assign({}, p); n[k] = v; return n; }); }
  var ok = form.name && form.category;
  return (
    <div>
      <Header title="Add New Client" sub="Enter details to begin the Brand Health Assessment" />
      <div style={{ background: C.cd, border: "1px solid " + C.bd, borderRadius: 10, padding: 22, maxWidth: 560 }}>
        <div style={{ display: "grid", gap: 14 }}>
          <FieldInput label="Brand Name *" value={form.name} onChange={function(v) { set("name", v); }} placeholder="e.g. FreshBrew Coffee" />
          <FieldInput label="Category *" value={form.category} onChange={function(v) { set("category", v); }} placeholder="e.g. FMCG - Beverages" />
          <FieldInput label="Website" value={form.website} onChange={function(v) { set("website", v); }} placeholder="e.g. freshbrew.in" />
          <FieldInput label="Revenue Range" value={form.revenue} onChange={function(v) { set("revenue", v); }} placeholder="e.g. 5-10 Cr" />
          <FieldInput label="Competitors" value={form.competitors} onChange={function(v) { set("competitors", v); }} placeholder="e.g. BluTokai, Third Wave" />
          <FieldInput label="Key Challenges" value={form.challenges} onChange={function(v) { set("challenges", v); }} placeholder="Top 2-3 brand challenges" multi />
          <button onClick={function() { if (ok) props.onSubmit(form); }} disabled={!ok} style={{ background: ok ? C.gd : C.el, color: ok ? C.bg : C.t3, border: "none", borderRadius: 6, padding: 12, fontFamily: "monospace", fontSize: 11, fontWeight: 600, cursor: ok ? "pointer" : "not-allowed" }}>
            CREATE AND BEGIN ASSESSMENT
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══ PREVIEW ═══
function PreviewView(props) {
  var c = props.client;
  var cs = props.composite(c);
  var sorted = DIMS.map(function(d) { return { nm: d.nm, ic: d.ic, s: props.dScore(c, d) }; }).sort(function(a, b) { return b.s - a.s; });
  var str = sorted[0];
  var wk = sorted[sorted.length - 1];
  return (
    <div>
      <Header title="Brand Health Score Preview" sub="Lead magnet for prospects" />
      <div style={{ background: C.cd, border: "1px solid " + C.gd + "25", borderRadius: 12, padding: 28, maxWidth: 640 }}>
        <div style={{ textAlign: "center", marginBottom: 24, paddingBottom: 18, borderBottom: "1px solid " + C.bd }}>
          <div style={{ fontFamily: "monospace", fontSize: 8, color: C.gd, letterSpacing: 3, marginBottom: 6 }}>BBP INDIA \u2014 BRAND HEALTH SCORE PREVIEW</div>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, margin: "0 0 4px" }}>{c.name}</h2>
          <div style={{ fontSize: 11, color: C.t2 }}>{c.category} | {new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</div>
        </div>
        <div style={{ textAlign: "center", margin: "0 0 24px" }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 64, fontWeight: 900, color: cs > 0 ? sColor(cs) : C.t3, lineHeight: 1 }}>{cs || "--"}</div>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: cs > 0 ? sColor(cs) : C.t3, letterSpacing: 2, marginTop: 5 }}>{cs > 0 ? sTier(cs).toUpperCase() : "PENDING"}</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 7, marginBottom: 20 }}>
          {DIMS.map(function(d) {
            var s = props.dScore(c, d);
            return (
              <div key={d.id} style={{ background: C.el, borderRadius: 6, padding: "10px 8px", textAlign: "center" }}>
                <div style={{ fontSize: 14, marginBottom: 2 }}>{d.ic}</div>
                <div style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 700, color: s > 0 ? sColor(s) : C.t3 }}>{s || "--"}</div>
                <div style={{ fontSize: 9, color: C.t3 }}>{d.sh}</div>
              </div>
            );
          })}
        </div>
        {cs > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
            <div style={{ background: C.gn + "08", border: "1px solid " + C.gn + "25", borderRadius: 8, padding: 14 }}>
              <div style={{ fontFamily: "monospace", fontSize: 7, color: C.gn, letterSpacing: 1.5, marginBottom: 4 }}>TOP STRENGTH</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{str.nm}</div>
              <div style={{ fontFamily: "monospace", fontSize: 18, color: C.gn, fontWeight: 700, marginTop: 2 }}>{str.s}/100</div>
            </div>
            <div style={{ background: C.rd + "08", border: "1px solid " + C.rd + "25", borderRadius: 8, padding: 14 }}>
              <div style={{ fontFamily: "monospace", fontSize: 7, color: C.rd, letterSpacing: 1.5, marginBottom: 4 }}>VULNERABILITY</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{wk.nm}</div>
              <div style={{ fontFamily: "monospace", fontSize: 18, color: C.rd, fontWeight: 700, marginTop: 2 }}>{wk.s}/100</div>
            </div>
          </div>
        )}
        <div style={{ background: C.gd + "10", border: "1px solid " + C.gd + "30", borderRadius: 8, padding: 20, textAlign: "center" }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, marginBottom: 5 }}>This is 20% of the full picture.</div>
          <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.5, marginBottom: 12 }}>The full diagnostic includes {TOTAL_Q} questions across 30 sub-metrics, competitive benchmarks, AI strategy, and an action roadmap.</div>
          <div style={{ fontFamily: "monospace", fontSize: 10, color: C.gd, letterSpacing: 1 }}>BOOK FULL DIAGNOSTIC | shine@bbp-india.com</div>
        </div>
      </div>
    </div>
  );
}
